/**
 * Daydra API client
 *
 * In dev: Vite proxy forwards /api → api.daydra.com
 * In prod: VITE_API_URL env var or same-origin
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "";

const REFRESH_TOKEN_KEY = "daydra_refresh_token";

// ── Token storage ──
// Access token: in-memory only (short-lived, no persistence needed)
// Refresh token: localStorage (survives page refresh)

let accessToken: string | null = null;
let onAuthExpired: (() => void) | null = null;

export function setTokens(access: string, refresh: string) {
  accessToken = access;
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  } catch {
    // Private browsing or storage full — refresh token stays in-memory only
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function clearTokens() {
  accessToken = null;
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // Ignore
  }
}

export function setOnAuthExpired(cb: () => void) {
  onAuthExpired = cb;
}

/**
 * Attempt to restore session from a persisted refresh token.
 * Returns true if session was restored, false otherwise.
 */
export async function tryRestoreSession(): Promise<boolean> {
  let storedRefresh: string | null = null;
  try {
    storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return false;
  }

  if (!storedRefresh) return false;

  try {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: storedRefresh }),
    });

    if (!res.ok) {
      // Refresh token expired or invalid — clear it
      clearTokens();
      return false;
    }

    const data: TokenResponse = await res.json();
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    // Network error — don't clear tokens, might be temporary
    return false;
  }
}

// ── HTTP helpers ──

async function refreshAccessToken(): Promise<boolean> {
  let storedRefresh: string | null = null;
  try {
    storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    // Fall through
  }

  if (!storedRefresh) return false;

  try {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: storedRefresh }),
    });
    if (!res.ok) return false;
    const data: TokenResponse = await res.json();
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  let res = await fetch(url, { ...options, headers });

  // If 401, attempt token refresh once
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed && accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
      res = await fetch(url, { ...options, headers });
    }
  }

  if (res.status === 401) {
    clearTokens();
    onAuthExpired?.();
    throw new ApiError("Session expired", 401);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.detail ?? res.statusText, res.status);
  }

  // 204 No Content — nothing to parse
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

// ── API methods ──

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface UserResponse {
  id: string;
  email: string;
  display_name: string | null;
  plan: string;
  created_at: string;
}

export interface ConnectedRepo {
  repo_full_name: string;
  default_branch: string;
}

export interface Project {
  id: string;
  name: string;
  description: string | null;
  connected_repo: ConnectedRepo | null;
  last_task: string | null;
  last_task_updated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Session {
  id: string;
  project_id: string;
  title: string | null;
  created_at: string;
  ended_at: string | null;
}

export interface MessageResponse {
  id: string;
  role: string;
  content: string;
  widget_json: Record<string, unknown> | null;
  activity_log: Record<string, unknown> | Array<Record<string, unknown>> | null;
  token_count: number | null;
  created_at: string;
}

export interface SessionDetail extends Session {
  messages: MessageResponse[];
}

export interface ProjectMessage {
  id: string;
  session_id: string;
  role: string;
  content: string;
  widget_json: Record<string, unknown> | null;
  activity_log: Record<string, unknown> | Array<Record<string, unknown>> | null;
  token_count: number | null;
  created_at: string;
}

export interface ActiveSession {
  id: string;
  project_id: string;
  title: string | null;
  model: string;
  created_at: string;
  is_new: boolean;
}

export interface GitHubInstallation {
  id: string;
  installation_id: number;
  account_login: string;
  account_type: string;
  suspended_at: string | null;
}

export interface GitHubRepo {
  id: number;
  full_name: string;
  default_branch: string;
  private: boolean;
}

export interface GitHubStatus {
  connected: boolean;
  installations: GitHubInstallation[];
}

export interface ProjectRepoResponse {
  id: string;
  project_id: string;
  github_repo_id: number;
  repo_full_name: string;
  default_branch: string;
}

export interface GitHubBranch {
  name: string;
  is_default: boolean;
  protected: boolean;
}

export interface BranchStatus {
  branch: string;
  success: boolean;
  error?: string;
}

export interface LogEntry {
  timestamp: string;
  message: string;
  source: string;
  level: string;
}

export interface CloudCredential {
  id: string;
  provider: string;
  label: string | null;
  has_token: boolean;
  created_at: string;
  updated_at: string;
}

export interface CloudDeployment {
  id: string;
  provider: string;
  provider_app_id: string;
  app_name: string;
  status: string;
  url: string | null;
  created_at: string;
  updated_at: string;
}

export interface AnimationControl {
  type: "slider" | "toggle" | "select";
  key: string;
  label: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  value: number | boolean | string;
  options?: string[];
}

export interface AnimationConfig {
  keyframes: string;
  properties: {
    duration: string;
    easing: string;
    delay: string;
    iterationCount: string;
    direction: string;
    fillMode: string;
  };
  demo: string;
  controls: AnimationControl[];
}

export const api = {
  auth: {
    login(email: string, password: string) {
      return request<TokenResponse>("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
    },
    register(email: string, password: string, display_name?: string) {
      return request<TokenResponse>("/api/v1/auth/register", {
        method: "POST",
        body: JSON.stringify({ email, password, display_name }),
      });
    },
    me() {
      return request<UserResponse>("/api/v1/auth/me");
    },
  },

  logs: {
    doDeployments() {
      return request<{ deployments: Array<{ id: string; phase: string; created_at: string; updated_at: string; cause: string }> }>("/api/v1/logs/do/deployments/");
    },
    doDeployLogs(deploymentId?: string) {
      const params = deploymentId ? `?deployment_id=${deploymentId}` : "";
      return request<{ entries: LogEntry[] }>(`/api/v1/logs/do/deploy/${params}`);
    },
    doRuntimeLogs() {
      return request<{ entries: LogEntry[] }>("/api/v1/logs/do/runtime/");
    },
    cfDeployments() {
      return request<{ deployments: Array<{ id: string; environment: string; created_on: string; latest_stage: string; deployment_trigger: string }> }>("/api/v1/logs/cf/deployments/");
    },
    cfBuildLogs(deploymentId?: string) {
      const params = deploymentId ? `?deployment_id=${deploymentId}` : "";
      return request<{ entries: LogEntry[] }>(`/api/v1/logs/cf/build/${params}`);
    },
    flyLogs() {
      return request<{ entries: LogEntry[] }>("/api/v1/logs/fly/");
    },
  },

  github: {
    status() {
      return request<GitHubStatus>("/api/v1/github/status/");
    },
    installations() {
      return request<GitHubInstallation[]>("/api/v1/github/installations/");
    },
    repos(installationId: string) {
      return request<GitHubRepo[]>(
        `/api/v1/github/installations/${installationId}/repos/`,
      );
    },
    disconnect(installationId: string) {
      return request<void>(
        `/api/v1/github/installations/${installationId}`,
        { method: "DELETE" },
      );
    },
    deleteProjectRepo(projectId: string) {
      return request<void>(
        `/api/v1/projects/${projectId}/github-repo`,
        { method: "DELETE" },
      );
    },
    oauthUrl() {
      return `${API_BASE}/auth/github?token=${accessToken}`;
    },
    branches(owner: string, repo: string) {
      return request<GitHubBranch[]>(
        `/api/v1/github/repos/${owner}/${repo}/branches/`,
      );
    },
    createRepo(body: {
      installation_id: string;
      name: string;
      private: boolean;
      description?: string;
    }) {
      return request<{ id: number; full_name: string; default_branch: string; private: boolean }>(
        "/api/v1/github/repos/",
        { method: "POST", body: JSON.stringify(body) },
      );
    },
  },

  projects: {
    list() {
      return request<Project[]>("/api/v1/projects/");
    },
    create(name: string, description?: string) {
      return request<Project>("/api/v1/projects/", {
        method: "POST",
        body: JSON.stringify({ name, description }),
      });
    },
    get(id: string) {
      return request<Project>(`/api/v1/projects/${id}`);
    },
    delete(id: string) {
      return request<void>(`/api/v1/projects/${id}`, { method: "DELETE" });
    },
    messages(projectId: string, opts?: { limit?: number; before?: string }) {
      const params = new URLSearchParams();
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.before) params.set("before", opts.before);
      const qs = params.toString();
      return request<ProjectMessage[]>(
        `/api/v1/projects/${projectId}/messages${qs ? `?${qs}` : ""}`,
      );
    },
    activeSession(projectId: string) {
      return request<ActiveSession>(`/api/v1/projects/${projectId}/active-session`);
    },
    connectRepo(
      projectId: string,
      body: {
        installation_id: string;
        github_repo_id: number;
        repo_full_name: string;
        default_branch: string;
      },
    ) {
      return request<ProjectRepoResponse>(
        `/api/v1/projects/${projectId}/repo`,
        { method: "POST", body: JSON.stringify(body) },
      );
    },
    disconnectRepo(projectId: string) {
      return request<void>(`/api/v1/projects/${projectId}/repo`, {
        method: "DELETE",
      });
    },
    currentBranch(projectId: string) {
      return request<BranchStatus>(`/api/v1/projects/${projectId}/branch/`);
    },
    listBranches(projectId: string) {
      return request<Array<{ name: string; is_current: boolean }>>(`/api/v1/projects/${projectId}/branches/`);
    },
    switchBranch(projectId: string, branch: string, create = false) {
      return request<BranchStatus>(`/api/v1/projects/${projectId}/branch/`, {
        method: "POST",
        body: JSON.stringify({ branch, create }),
      });
    },
  },

  sessions: {
    list(projectId: string) {
      return request<Session[]>(`/api/v1/projects/${projectId}/sessions/`);
    },
    create(projectId: string, title?: string) {
      return request<Session>(`/api/v1/projects/${projectId}/sessions/`, {
        method: "POST",
        body: JSON.stringify({ title }),
      });
    },
    get(projectId: string, sessionId: string) {
      return request<SessionDetail>(
        `/api/v1/projects/${projectId}/sessions/${sessionId}`,
      );
    },
  },

  cloud: {
    // User-level credentials (shared across all projects)
    listCredentials() {
      return request<CloudCredential[]>(
        `/api/v1/user/cloud/credentials/`,
      );
    },
    createCredential(
      body: { provider: string; token: string; label?: string },
    ) {
      return request<CloudCredential>(
        `/api/v1/user/cloud/credentials/`,
        { method: "POST", body: JSON.stringify(body) },
      );
    },
    deleteCredential(credentialId: string) {
      return request<void>(
        `/api/v1/user/cloud/credentials/${credentialId}`,
        { method: "DELETE" },
      );
    },
    // Project-level deployments (still per-project)
    listDeployments(projectId: string) {
      return request<CloudDeployment[]>(
        `/api/v1/projects/${projectId}/cloud/deployments/`,
      );
    },
    destroyDeployment(projectId: string) {
      return request<void>(
        `/api/v1/projects/${projectId}/cloud/deployment`,
        { method: "DELETE" },
      );
    },
    // DigitalOcean OAuth
    digitalOceanOAuthUrl() {
      return `${API_BASE}/auth/digitalocean?token=${accessToken}`;
    },
    digitalOceanStatus() {
      return request<{ connected: boolean; label: string | null; oauth: boolean }>(
        `/api/v1/user/cloud/digitalocean/status/`,
      );
    },
    // Check daydra.app subdomain availability (used by the deploy widget).
    checkSubdomain(subdomain: string) {
      return request<{
        subdomain: string;
        normalized: string;
        available: boolean;
        reason: string | null;
        full_domain: string | null;
        suggestions: string[];
      }>(`/api/v1/user/cloud/subdomain/check/`, {
        method: "POST",
        body: JSON.stringify({ subdomain }),
      });
    },
  },

  svg: {
    generate(prompt: string) {
      return request<{ svg: string }>("/api/v1/svg/generate/", {
        method: "POST",
        body: JSON.stringify({ prompt }),
      });
    },
    iterate(currentSvg: string, prompt: string) {
      return request<{ svg: string }>("/api/v1/svg/iterate/", {
        method: "POST",
        body: JSON.stringify({ current_svg: currentSvg, prompt }),
      });
    },
  },

  animation: {
    generate(prompt: string) {
      return request<{ config: AnimationConfig }>("/api/v1/animation/generate/", {
        method: "POST",
        body: JSON.stringify({ prompt }),
      });
    },
    iterate(currentConfig: AnimationConfig, prompt: string) {
      return request<{ config: AnimationConfig }>("/api/v1/animation/iterate/", {
        method: "POST",
        body: JSON.stringify({ current_config: currentConfig, prompt }),
      });
    },
  },
};
